import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { CreateProjectRequestPost, HeatLoadSelectRequestPost, UpdateProjectRequestPartial } from '@nx-customer-apps/api-planning-projects';
import { AppState, PdfReportGenerationParams } from '@nx-customer-apps/shared/interfaces';
import { ProjectActions } from './project.actions';
import { ProjectSelectors } from './project.selectors';
import {
    HeatLoadCalculationMethod,
    LanguageCodes,
    ReportConfigurationSection,
    ReportType,
    RouterLinks
} from '@nx-customer-apps/shared/enums';

@Injectable({ providedIn: 'root' })
export class ProjectStore {
    public state$ = this.store$.pipe(select(ProjectSelectors.getState()));
    public project$ = this.store$.pipe(select(ProjectSelectors.selectProject()));
    public selectedVariantId$ = this.store$.pipe(select(ProjectSelectors.selectSelectedVariantId()));
    public hasSystemComponentsOrHeatloads$ = this.store$.pipe(select(ProjectSelectors.selectHasSystemComponentsOrHeatloads()));
    public addressCollision$ = this.store$.pipe(select(ProjectSelectors.selectAddressCollision()));
    public address$ = this.store$.pipe(select(ProjectSelectors.selectAddress()));

    constructor(private store$: Store<AppState>) {}

    getProject(payload: { id: string }): void {
        this.store$.dispatch(ProjectActions.getProject(payload));
    }

    createProject(payload: { request: CreateProjectRequestPost }): void {
        this.store$.dispatch(ProjectActions.createProject(payload));
    }

    updateProject(payload: { id: string; request: UpdateProjectRequestPartial; redirectUrl?: string }): void {
        const { id, request, redirectUrl } = payload;
        const actionOptions = { id, request, successAction: ProjectActions.getProjectSuccess, successActionOptions: { redirectUrl } };
        this.store$.dispatch(ProjectActions.updateProject(actionOptions));
    }

    updateProjectEndUser(payload: {
        id: string;
        endUserPayload: UpdateProjectRequestPartial;
        pdfReportGenerationPayload: PdfReportGenerationParams;
    }): void {
        this.store$.dispatch(ProjectActions.updateProjectEndUser(payload));
    }

    verifyProject(payload: { id: string; request: UpdateProjectRequestPartial; callback?: () => void }): void {
        const { id, request, callback } = payload;
        const actionOptions = {
            id,
            request,
            successAction: ProjectActions.migrationVerificationSuccess,
            successActionOptions: { callback }
        };
        this.store$.dispatch(ProjectActions.updateProject(actionOptions));
    }

    beforeUpdateProject(payload: { id: string; request: UpdateProjectRequestPartial; redirectUrl?: string }): void {
        this.store$.dispatch(ProjectActions.beforeUpdateProject(payload));
    }

    addressCollision(): void {
        this.store$.dispatch(ProjectActions.addressCollision());
    }

    savePhotovoltaicIntermediary(payload: { variantId: string; request: UpdateProjectRequestPartial }): void {
        this.store$.dispatch(ProjectActions.savePhotovoltaicIntermediary(payload));
    }

    saveHeatPumpIntermediary(payload: { id: string; request: UpdateProjectRequestPartial; variantId: string }): void {
        const { id, request, variantId } = payload;
        const actionOptions = { id, request, successAction: ProjectActions.getHeatPumpTools, successActionOptions: { variantId } };
        this.store$.dispatch(ProjectActions.updateProject(actionOptions));
    }

    saveHeatload(payload: { projectPatch: UpdateProjectRequestPartial; redirectUrl: string }): void {
        this.store$.dispatch(ProjectActions.saveHeatload(payload));
    }

    closeProject(projectId: string): void {
        this.store$.dispatch(ProjectActions.closeProject({ projectId }));
    }

    completeProject(projectId: string): void {
        this.store$.dispatch(ProjectActions.completeProject({ projectId }));
    }

    reloadProject(projectId: string): void {
        this.store$.dispatch(
            ProjectActions.reloadProject({
                id: projectId,
                successActionOptions: { redirectUrl: `${RouterLinks.Project}${projectId}` },
                successAction: ProjectActions.getProjectSuccess
            })
        );
    }

    uploadProjectReportSection(payload: {
        body: string;
        projectId: string;
        section: ReportConfigurationSection;
        applicationVersion: string;
    }): void {
        this.store$.dispatch(ProjectActions.uploadProjectReportSection(payload));
    }

    pdfReportGeneration(params: PdfReportGenerationParams): void {
        this.store$.dispatch(ProjectActions.pdfReportGeneration({ params }));
    }
}
